import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus,
  Download,
  Edit,
  Trash2,
  Copy,
  Save,
  Eye
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// Form validation schema
const reportFormSchema = z.object({
  name: z.string().min(1, 'Report name is required'),
  description: z.string().min(1, 'Description is required'),
  type: z.enum(['player', 'team', 'academy', 'session']),
  format: z.enum(['pdf', 'excel', 'csv']),
  dateRange: z.object({
    start: z.string(),
    end: z.string()
  }),
  metrics: z.array(z.string()).min(1, 'At least one metric must be selected'),
  players: z.array(z.string()).optional(),
  includeCharts: z.boolean(),
  includeRecommendations: z.boolean()
});

type ReportFormData = z.infer<typeof reportFormSchema>;

interface CustomReport {
  id: string;
  name: string;
  description: string;
  type: 'player' | 'team' | 'academy' | 'session';
  format: 'pdf' | 'excel' | 'csv';
  createdDate: string;
  lastModified: string;
  createdBy: string;
  status: 'draft' | 'saved' | 'generated';
  metrics: string[];
  playerCount?: number;
}

const mockCustomReports: CustomReport[] = [
  {
    id: '1',
    name: 'Q1 Performance Analysis',
    description: 'Comprehensive quarterly performance review for all players',
    type: 'academy',
    format: 'pdf',
    createdDate: '2024-01-10T10:00:00Z',
    lastModified: '2024-01-12T14:30:00Z',
    createdBy: 'Coach Sarah Wilson',
    status: 'saved',
    metrics: ['distance', 'consistency', 'improvement_rate', 'technique_score'],
    playerCount: 24
  },
  {
    id: '2',
    name: 'Individual Player Deep Dive',
    description: 'Detailed analysis for top 5 performing players',
    type: 'player',
    format: 'excel',
    createdDate: '2024-01-08T09:15:00Z',
    lastModified: '2024-01-08T09:15:00Z',
    createdBy: 'Coach Mike Johnson',
    status: 'generated',
    metrics: ['shot_distance', 'bat_speed', 'timing', 'placement_accuracy'],
    playerCount: 5
  },
  {
    id: '3',
    name: 'Session Effectiveness Study',
    description: 'Analysis of training session effectiveness over 30 days',
    type: 'session',
    format: 'csv',
    createdDate: '2024-01-05T16:20:00Z',
    lastModified: '2024-01-06T11:45:00Z',
    createdBy: 'Coach Sarah Wilson',
    status: 'draft',
    metrics: ['session_duration', 'player_engagement', 'skill_improvement', 'drill_effectiveness']
  }
];

const availableMetrics = [
  { id: 'shot_distance', label: 'Shot Distance', category: 'Performance' },
  { id: 'bat_speed', label: 'Bat Speed', category: 'Performance' },
  { id: 'timing', label: 'Timing Score', category: 'Technique' },
  { id: 'placement_accuracy', label: 'Placement Accuracy', category: 'Technique' },
  { id: 'consistency', label: 'Consistency Score', category: 'Performance' },
  { id: 'improvement_rate', label: 'Improvement Rate', category: 'Progress' },
  { id: 'technique_score', label: 'Overall Technique', category: 'Technique' },
  { id: 'session_attendance', label: 'Session Attendance', category: 'Engagement' },
  { id: 'goal_achievement', label: 'Goal Achievement', category: 'Progress' },
  { id: 'coach_feedback', label: 'Coach Feedback Score', category: 'Assessment' }
];

const mockPlayers = [
  { id: '1', name: 'Alex Thompson' },
  { id: '2', name: 'Sarah Chen' },
  { id: '3', name: 'Michael Rodriguez' },
  { id: '4', name: 'Emma Wilson' },
  { id: '5', name: 'James Parker' }
];

export const CustomReports: React.FC = () => {
  const [_isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [_selectedReport, setSelectedReport] = useState<CustomReport | null>(null);
  const [activeTab, setActiveTab] = useState('templates');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<ReportFormData>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      metrics: [],
      players: [],
      includeCharts: true,
      includeRecommendations: true,
      dateRange: {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      }
    }
  });

  const watchedMetrics = watch('metrics') || [];
  const watchedType = watch('type');

  const onSubmit = (data: ReportFormData) => {
    console.log('Creating custom report:', data);
    setIsCreateDialogOpen(false);
    // In real implementation, this would create the report
  };

  const handleMetricToggle = (metricId: string) => {
    const currentMetrics = watchedMetrics;
    const newMetrics = currentMetrics.includes(metricId)
      ? currentMetrics.filter(id => id !== metricId)
      : [...currentMetrics, metricId];
    setValue('metrics', newMetrics);
  };

  const handleGenerateReport = (reportId: string) => {
    console.log('Generating report:', reportId);
    // In real implementation, this would trigger report generation
  };

  const handleEditReport = (report: CustomReport) => {
    setSelectedReport(report);
    setIsCreateDialogOpen(true);
    // Pre-populate form with report data
  };

  const handleDeleteReport = (reportId: string) => {
    console.log('Deleting report:', reportId);
    // In real implementation, this would delete the report
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: CustomReport['status']) => {
    switch (status) {
      case 'draft': return 'outline';
      case 'saved': return 'secondary';
      case 'generated': return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Custom Reports</h2>
          <p className="text-muted-foreground">Create and manage custom analytics reports</p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Create Report</span>
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="templates">My Reports</TabsTrigger>
          <TabsTrigger value="generator">Report Builder</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-6">
          {/* Reports Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {mockCustomReports.map((report) => (
              <Card key={report.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{report.name}</CardTitle>
                      <Badge variant={getStatusColor(report.status)} className="mt-1">
                        {report.status}
                      </Badge>
                    </div>
                    <div className="flex space-x-1">
                      <Button size="sm" variant="outline" onClick={() => handleEditReport(report)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteReport(report.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <CardDescription className="text-sm mt-2">
                    {report.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Type</div>
                      <div className="font-medium capitalize">{report.type}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Format</div>
                      <div className="font-medium">{report.format.toUpperCase()}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Created</div>
                      <div className="font-medium">{formatDate(report.createdDate)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Modified</div>
                      <div className="font-medium">{formatDate(report.lastModified)}</div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">Metrics ({report.metrics.length})</div>
                    <div className="flex flex-wrap gap-1">
                      {report.metrics.slice(0, 3).map((metric) => (
                        <Badge key={metric} variant="outline" className="text-xs">
                          {availableMetrics.find(m => m.id === metric)?.label || metric}
                        </Badge>
                      ))}
                      {report.metrics.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{report.metrics.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex space-x-2 pt-2 border-t">
                    <Button size="sm" onClick={() => handleGenerateReport(report.id)} className="flex-1">
                      <Download className="w-4 h-4 mr-2" />
                      Generate
                    </Button>
                    <Button size="sm" variant="outline">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="generator">
          <Card>
            <CardHeader>
              <CardTitle>Report Builder</CardTitle>
              <CardDescription>
                Build a custom report with your preferred metrics and filters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Report Name</Label>
                    <Input
                      id="name"
                      {...register('name')}
                      placeholder="Enter report name"
                    />
                    {errors.name && (
                      <p className="text-sm text-red-500">{errors.name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="type">Report Type</Label>
                    <Select onValueChange={(value) => setValue('type', value as any)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select report type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="player">Player Report</SelectItem>
                        <SelectItem value="team">Team Report</SelectItem>
                        <SelectItem value="academy">Academy Report</SelectItem>
                        <SelectItem value="session">Session Report</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    {...register('description')}
                    placeholder="Describe what this report will include"
                    rows={3}
                  />
                  {errors.description && (
                    <p className="text-sm text-red-500">{errors.description.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="format">Export Format</Label>
                    <Select onValueChange={(value) => setValue('format', value as any)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pdf">PDF</SelectItem>
                        <SelectItem value="excel">Excel</SelectItem>
                        <SelectItem value="csv">CSV</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      {...register('dateRange.start')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end-date">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      {...register('dateRange.end')}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <Label>Select Metrics</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableMetrics.map((metric) => (
                      <div key={metric.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={metric.id}
                          checked={watchedMetrics.includes(metric.id)}
                          onCheckedChange={() => handleMetricToggle(metric.id)}
                        />
                        <Label htmlFor={metric.id} className="text-sm">
                          {metric.label}
                          <span className="text-xs text-muted-foreground ml-1">
                            ({metric.category})
                          </span>
                        </Label>
                      </div>
                    ))}
                  </div>
                  {errors.metrics && (
                    <p className="text-sm text-red-500">{errors.metrics.message}</p>
                  )}
                </div>

                {watchedType === 'player' && (
                  <div className="space-y-2">
                    <Label>Select Players (Optional)</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {mockPlayers.map((player) => (
                        <div key={player.id} className="flex items-center space-x-2">
                          <Checkbox id={`player-${player.id}`} />
                          <Label htmlFor={`player-${player.id}`} className="text-sm">
                            {player.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <Label>Report Options</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="include-charts"
                        {...register('includeCharts')}
                      />
                      <Label htmlFor="include-charts">Include Charts and Visualizations</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="include-recommendations"
                        {...register('includeRecommendations')}
                      />
                      <Label htmlFor="include-recommendations">Include AI Recommendations</Label>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button type="submit" className="flex items-center space-x-2">
                    <Save className="w-4 h-4" />
                    <span>Save Report</span>
                  </Button>
                  <Button type="button" variant="outline" className="flex items-center space-x-2">
                    <Eye className="w-4 h-4" />
                    <span>Preview</span>
                  </Button>
                  <Button type="button" variant="outline" className="flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>Generate Now</span>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};